// ProjectTypeForm.tsx
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { ProjectType } from "../../../types/projectType";
import api from "../../../services/axiosInstance";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Button from "../../../shared/components/ui/button/Button";

interface ProjectTypeFormProps {
  editingItem: ProjectType | null;
  onAdd: (item: ProjectType) => void;
  onClose: () => void;
}

export default function ProjectTypeForm({ 
  editingItem, 
  onAdd, 
  onClose 
}: ProjectTypeFormProps) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    reset,
    watch,
    formState: { errors, isDirty, isValid },
    setFocus
  } = useForm<ProjectType>({
    defaultValues: {
      name: "",
      description: "",
      status: "active",
      category: undefined as unknown as any
    },
    mode: "onChange"
  });

  const watchedName = watch("name");
  const watchedDescription = (watch("description") as string) || "";

  // Set form values when editing
  useEffect(() => {
    if (editingItem) {
      reset({
        name: editingItem.name,
        description: editingItem.description,
        status: editingItem.status,
        category: editingItem.category
      });
    } else {
      reset();
    }
    setFocus("name");
  }, [editingItem, reset, setFocus]);

  const onSubmit = async (data: ProjectType) => {
    setIsSubmitting(true);
    setApiError(null);
    setShowSuccess(false);
    
    try {
      let res;
      const payload: any = {
        name: data.name.trim(),
        category: data.category,
        status: "active"
      };
      
      // Only include description if it has a value
      if (data.description?.trim()) {
        payload.description = data.description.trim();
      }
      
      if (editingItem) {
        res = await api.patch(`/project-types/${editingItem._id}`, payload);
      } else {
        res = await api.post("/project-types", payload);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        onAdd(res.data);
        onClose();
      }, 1000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || "An error occurred while saving the project type");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // const formTitle = editingItem ? "Edit Project Type" : "Add New Project Type"; // Kept for future use

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              {editingItem ? "Project type updated successfully!" : "Project type created successfully!"}
            </p>
          </div>
        </div>
      )}
      
      {apiError && (
        <ErrorAlert 
          message={apiError} 
          onClose={() => setApiError(null)} 
          className="mb-4"
        />
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Category Field */}
        <div className="space-y-2">
          <label 
            htmlFor="category" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Category *
          </label>
          <div className="relative">
            <select
              id="category"
              {...register("category", { required: "Category is required" })}
              className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                errors.category
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
              }`}
              defaultValue=""
            >
              <option value="" disabled>
                Select category
              </option>
              <option value="solar">Solar</option>
              <option value="wind">Wind</option>
              <option value="hybrid">Hybrid</option>
              <option value="bess">BESS</option>
            </select>
          </div>
          {errors.category && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.category.message as string}
            </p>
          )}
        </div>
        {/* Name Field */}
        <div className="space-y-2">
          <label 
            htmlFor="name" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Project Type Name *
          </label>
          <div className="relative">
            <input
              id="name"
              type="text"
              {...register("name", { 
                required: "Project type name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters"
                },
                maxLength: {
                  value: 50,
                  message: "Name must be less than 50 characters"
                },
              })}
              className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                errors.name
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : watchedName && !errors.name
                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
              }`}
              placeholder="Enter project type name"
            />
            {watchedName && !errors.name && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
            {errors.name && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
            )}
          </div>
          {errors.name && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.name.message}
            </p>
          )}
          {watchedName && !errors.name && (
            <p className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Name looks good!
            </p>
          )}
        </div>
        
        {/* Description Field */}
        <div className="space-y-2">
          <label 
            htmlFor="description" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Description
            <span className="text-gray-400 font-normal ml-1">(optional)</span>
          </label>
          <div className="relative">
            <textarea
              id="description"
              rows={4}
              {...register("description", {
                maxLength: {
                  value: 200,
                  message: "Description must be less than 200 characters"
                }
              })}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${
                errors.description 
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : watchedDescription && !errors.description
                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
              }`}
              placeholder="Enter a brief description of this project type..."
            />
            {watchedDescription && !errors.description && (
              <CheckCircle2 className="absolute right-3 top-4 w-5 h-5 text-green-500" />
            )}
            {errors.description && (
              <AlertCircle className="absolute right-3 top-4 w-5 h-5 text-red-500" />
            )}
          </div>
          <div className="flex items-center justify-between">
            {errors.description && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.description.message}
              </p>
            )}
            {watchedDescription && !errors.description && (
              <p className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Description looks good!
              </p>
            )}
            <span className={`text-xs ${
              (watchedDescription?.length || 0) > 180 ? 'text-red-500' : 
              (watchedDescription?.length || 0) > 150 ? 'text-yellow-500' : 'text-gray-400'
            }`}>
              {watchedDescription?.length || 0}/200
            </span>
          </div>
        </div>
        
        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row justify-end gap-3 pt-5 pb-2 border-t border-gray-200 mt-6 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="sorigin"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingItem ? "Updating..." : "Creating..."}
              </div>
            ) : editingItem ? (
              "Update Project Type"
            ) : (
              "Create Project Type"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}